import { ensureFreshSession } from "./auth";
import type { RuntimeConfig } from "./config";

export interface JobSummary {
  readonly jobId: string;
  readonly status: string;
  readonly sourceFileName: string;
  readonly title?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly errorMessage?: string;
  readonly hasEpub?: boolean;
}

const apiFetch = async (
  config: RuntimeConfig,
  path: string,
  init: RequestInit = {},
): Promise<Response> => {
  const session = await ensureFreshSession(config);
  if (!session) throw new Error("Not signed in");
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${session.idToken}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(`${config.apiUrl}${path}`, { ...init, headers });
};

export const listJobs = async (config: RuntimeConfig): Promise<JobSummary[]> => {
  const response = await apiFetch(config, "/jobs");
  if (!response.ok) throw new Error("Failed to list jobs");
  const body = (await response.json()) as { jobs: JobSummary[] };
  return body.jobs;
};

export const getJob = async (config: RuntimeConfig, jobId: string): Promise<JobSummary> => {
  const response = await apiFetch(config, `/jobs/${jobId}`);
  if (!response.ok) throw new Error("Failed to load job");
  return (await response.json()) as JobSummary;
};

export const createUploadJob = async (
  config: RuntimeConfig,
  fileName: string,
): Promise<{ jobId: string; uploadUrl: string }> => {
  const response = await apiFetch(config, "/jobs", {
    method: "POST",
    body: JSON.stringify({ fileName, contentType: "application/pdf" }),
  });
  if (!response.ok) throw new Error("Failed to create job");
  return (await response.json()) as { jobId: string; uploadUrl: string };
};

export const startJob = async (config: RuntimeConfig, jobId: string): Promise<void> => {
  const response = await apiFetch(config, `/jobs/${jobId}/start`, { method: "POST" });
  if (!response.ok) throw new Error("Failed to start job");
};

export const downloadJob = async (
  config: RuntimeConfig,
  jobId: string,
): Promise<{ downloadUrl: string; fileName: string }> => {
  const response = await apiFetch(config, `/jobs/${jobId}/download`);
  if (!response.ok) throw new Error("EPUB not ready");
  return (await response.json()) as { downloadUrl: string; fileName: string };
};

export const uploadAndConvert = async (config: RuntimeConfig, file: File): Promise<string> => {
  const created = await createUploadJob(config, file.name);
  const put = await fetch(created.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/pdf" },
    body: file,
  });
  if (!put.ok) throw new Error("Upload to S3 failed");
  await startJob(config, created.jobId);
  return created.jobId;
};
